/* eslint-disable jsx-a11y/anchor-has-content */

import {
	NotificationBanner,
	Text,
	TextLink,
} from '@audio-network/componentlibrary'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import NewProjectModal from '@/components/shared/NewProjectModal/NewProjectModal'
import { TrialState } from '@/constants/user/userTrialState'
import sessionStore from '@/stores/sessionStore'
import clientGetToggleFromCookie from '@/utils/cookies/getToggleFromCookie'

import Header from '../Header/Header'
import Main from '../Main/Main'
import Sidebar from '../Sidebar/Sidebar'
import styles from './PrimaryLayout.module.css'
import { PrimaryLayoutProps } from './PrimaryLayout.types'

// To avoid hydration errors related to the volume component making use of localStorage lets only render Player on the Client
// more info can be found here: https://nextjs.org/docs/messages/react-hydration-error
const PackagedPlayer = dynamic<{
	interfaceText: Record<string, string>
}>(() => import('@audio-network/player').then((mod) => mod.Player), {
	ssr: false,
})

const MusicAppPlayer = dynamic(
	() => import('@/components/layout/Player/Player'),
	{
		ssr: false,
	}
)

const PrimaryLayout: React.FC<PrimaryLayoutProps> = ({
	children,
	translations,
	...props
}) => {
	const user = sessionStore((state) => state.session?.user)
	const trialState = user?.organisation?.trialState
	const router = useRouter()
	const { t } = useTranslation(['app'])
	const [isMusicAppPlayerEnabled, setIsMusicAppPlayerEnabled] = useState(false)

	useEffect(() => {
		setIsMusicAppPlayerEnabled(!!clientGetToggleFromCookie('MusicAppPlayer'))
	}, [])

	const trialBanner = {
		[TrialState.ActiveWithDownloads]: {
			message: t('active-trial-banner-message'),
			link: '/support/trial',
		},
		[TrialState.ActiveNoDownloads]: {
			message: t('end-trial-banner-message'),
			link: '/contact',
		},
	}

	const bannerMessage = trialBanner[trialState]?.message
	const bannerLink = trialBanner[trialState]?.link

	const handlePlayerClick = (event: React.MouseEvent<HTMLDivElement>): void => {
		/**
		 * Logged out users should be directed to the ACQ app
		 */
		if (!user) {
			return
		}

		const target = event.target as HTMLAnchorElement
		const href = (target.tagName === 'A' ? target : target.closest('a'))?.href

		if (href) {
			event.preventDefault()
			router.push(href)
		}
	}

	const newProjectModalId = 'new-project-modal'

	return (
		<div className={styles.layout} {...props}>
			<Header />
			{bannerMessage && (
				<NotificationBanner id="global-banner" className={styles.banner}>
					<Text>
						<Trans
							components={{
								a: (
									<TextLink
										target="_blank"
										rel="noopener noreferrer"
										href={bannerLink}
									/>
								),
							}}
						>
							{bannerMessage}
						</Trans>
					</Text>
				</NotificationBanner>
			)}
			<Sidebar />
			<Main>
				{children}
				{user && <NewProjectModal id={newProjectModalId} />}
			</Main>
			{/* TODO: Player component should accept styles, onClicks etc. That will remove need for ignore rules below */}
			{isMusicAppPlayerEnabled ? (
				<MusicAppPlayer />
			) : (
				// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
				<div className={styles.player} onClick={handlePlayerClick}>
					<PackagedPlayer interfaceText={translations} />
				</div>
			)}
		</div>
	)
}

export default PrimaryLayout
